import React, { useState } from 'react';

// Utils
import { useQuery, useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import { SortDirection } from 'ka-table/enums';
import { useMediaQuery } from 'react-responsive';
import withAuth from '../../utils/hocs/withAuth';
import withSidebar from '../../utils/hocs/withSidebar';
import SCOPE from '../../utils/gate/scope';
import withPermission from '../../utils/hocs/withPermission';
import { isGranted } from '../../utils/gate/permissionsGate';
import { useAppContext } from '../../store/AppContext';
import useNotification from '../../utils/hooks/useNotification';

// Services
import useEstablishmentService from '../../services/establishmentService';

// Icons
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';

// Components
import PageWrapper from '../../components/layout/PageWrapper/PageWrapper';
import SkeletonLoader from '../../components/molecules/SkeletonLoader/SkeletonLoader';
import GenericTable from '../../components/tables/GenericTable/GenericTable';
import EmptyList from '../../components/organisms/EmptyList/EmptyList';
import Tag from '../../components/atoms/Tag/Tag';
import TagWithEdition from '../../components/atoms/TagWithEdition/TagWithEdition';
import Button from '../../components/atoms/Button/Button';
import useModal from '../../utils/hooks/useModal';
import CreateEstablishmentModal from './CreateEstablishmentModal/CreateEstablishmentModal';

function EstablishmentList() {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const [{ user }] = useAppContext();

  const [establishments, setEstablishments] = useState([]);

  const { getAllEstablishments, postUpdateStatus } = useEstablishmentService();
  const { displayNotification } = useNotification();
  const { displayModal } = useModal();

  const { mutate: statusMutate } = useMutation(
    ({ establishmentId, isActive }) => postUpdateStatus({ establishmentId, isActive }),
    {
      onMutate: (updated) => {
        setEstablishments((state) => state.map((establishment) => {
          if (establishment.id === updated.establishmentId) {
            return { ...establishment, isActive: updated.isActive };
          }
          return establishment;
        }));
      },
      onError: (_, updated) => {
        setEstablishments((state) => state.map((establishment) => {
          if (establishment.id === updated.establishmentId) {
            return { ...establishment, isActive: !updated.isActive };
          }
          return establishment;
        }));
      },
      onSuccess: (_, updated) => {
        const establishment = establishments?.find((est) => est.id === updated.establishmentId);
        if (establishment) {
          displayNotification({
            message: t('establishments.table.actions.status.success', { establishment: establishment.name }),
          });
        }
      },
    },
  );

  const { isFetching, refetch: refetchEstablishments } = useQuery(
    ['getAllEstablishments'],
    () => getAllEstablishments(),
    {
      onSuccess: (data) => {
        if (data?.data?.establishments?.length) {
          setEstablishments(data.data.establishments);
        }
      },
    },
  );

  const columns = [
    {
      key: 'name', title: t('establishments.table.columns.name'), sortDirection: SortDirection.Ascend, bold: true,
    },
    { key: 'status', title: t('establishments.table.columns.status') },
  ];

  const getEstablishmentRow = (establishment, index) => ({
    id: index,
    name: establishment.name,
    status: (
      isGranted(user, [SCOPE.activate_pla_establishment, SCOPE.deactivate_pla_establishment]) ? (
        <TagWithEdition
          theme={establishment.isActive ? 'green' : 'yellow'}
          label={t(`tables.status.${establishment.isActive ? 'active' : 'inactive'}`)}
          options={[
            { value: 'active', label: t('tables.status.active') },
            { value: 'inactive', label: t('tables.status.inactive') },
          ]}
          onChange={(status) => {
            statusMutate({ establishmentId: establishment.id, isActive: status === 'active' });
          }}
          activeValue={establishment.isActive ? 'active' : 'inactive'}
        />
      ) : (
        <Tag
          theme={establishment.isActive ? 'green' : 'yellow'}
          label={t(`tables.status.${establishment.isActive ? 'active' : 'inactive'}`)}
        />
      )
    ),
  });

  return (
    <PageWrapper
      title={t('establishments.title')}
      hasWhiteWrapper
      ctaButton={(
        <Button
          full={isMobile}
          onClick={() => displayModal({
            maxWidth: 455,
            title: t('establishments.table.add.title'),
            content: (
              <CreateEstablishmentModal refetchEstablishments={refetchEstablishments} />
            ),
          })}
        >
          <PlusIcon className="m-r-10" />
          {t('establishments.table.add.title')}
        </Button>
      )}
    >
      {isFetching ? (
        <>
          <SkeletonLoader height={60} className="m-b-20" />
          <SkeletonLoader height={300} />
        </>
      ) : (
        establishments?.length ? (
          <GenericTable
            columns={columns}
            rows={establishments.map((establishment, index) => getEstablishmentRow(establishment, index))}
            isSearchable
            searchPlaceholder={t('establishments.table.search')}
          />
        ) : (
          <EmptyList
            imageTheme="establishment"
            title={t('establishments.table.empty.title')}
            text={`
              ${t('establishments.table.empty.text')} 
              ${isGranted(user, [SCOPE.create_pla_establishment]) ? t('establishments.table.empty.add') : ''}
            `}
          />
        )
      )}
    </PageWrapper>
  );
}

EstablishmentList.propTypes = {};

export default withAuth(
  withPermission(
    withSidebar(EstablishmentList),
    {
      scopes: [
        SCOPE.get_pla_establishment,
      ],
    },
  ),
);
